import React, { useState, useContext, ContextType } from "react";
import { Context } from "..";
import DateSelect from "./DateSelect";
import { useTranslation } from "react-i18next";
import { useToasts } from "react-toast-notifications";

type InputProps = {
  updatingTransaction?:
    | ContextType<typeof Context>["transactions"][0][0]
    | null;
  onClose?: () => void;
};

const Input: React.FC<InputProps> = ({
  onClose = () => {},
  updatingTransaction: ut,
}) => {
  const { t } = useTranslation();
  const [type, setType] = useState<"INCOME" | "EXPENSE">(
    ut ? (ut.value < 0 ? "EXPENSE" : "INCOME") : "EXPENSE"
  );
  const [value, setValue] = useState(ut ? Math.abs(ut.value) + "" : "");
  const [newDescription, setNewDescription] = useState(
    ut ? ut.description : ""
  );
  const {
    transactions,
    events,
    incomeCategories,
    expenseCategories,
    currentView,
  } = useContext(Context);

  const now = new Date();
  const [date, setDate] = useState(
    ut
      ? {
          day: ut.date.getDate(),
          computerMonth: ut.date.getMonth(),
          year: ut.date.getFullYear(),
        }
      : {
          day: now.getDate(),
          computerMonth: now.getMonth(),
          year: now.getFullYear(),
        }
  );

  const dateChange = React.useCallback(
    (arg: { day: number; computerMonth: number; year: number }) => {
      setDate(arg);
    },
    []
  );

  const [hours, setHours] = useState(ut ? ut.date.getHours() : now.getHours());
  const [minutes, setMinutes] = useState(ut ? ut.date.getMinutes() : 0);

  const [attachTo, setAttachTo] = useState<"CATEGORY" | "EVENT">(
    ut ? ut.attachedTo.type : "CATEGORY"
  );
  const [attachToId, setAttachToId] = useState<null | string>(
    ut ? ut.attachedTo.id : null
  );

  const { addToast } = useToasts();

  const upsertTransaction = () => {
    if (attachToId === null) {
      alert("attachToId is null");
      return;
    }

    const convertedValue = value.replace(",", ".");

    const newTransaction = {
      date: new Date(date.year, date.computerMonth, date.day, hours, minutes),
      value:
        type === "EXPENSE"
          ? -Math.abs(parseFloat(convertedValue) || 0)
          : Math.abs(parseFloat(convertedValue) || 0),
      attachedTo: {
        type: attachTo,
        id: attachToId,
      },
      description: newDescription,
    };

    if (ut) {
      transactions[1]((transactions) => {
        const transactionsCopy = [...transactions];
        const foundIndex = transactions.findIndex((t) => t.id === ut.id);

        if (foundIndex > -1) {
          transactionsCopy[foundIndex] = {
            ...transactionsCopy[foundIndex],
            ...newTransaction,
          };
          return transactionsCopy;
        }

        return transactions;
      });
    } else {
      transactions[1]((transactions) => {
        return [
          ...transactions,
          {
            id: Math.round(Math.random() * 100000) + "",
            ...newTransaction,
          },
        ];
      });
    }

    onClose();

    addToast(
      ut ? (
        t("Transaction successfuly updated")
      ) : (
        <div>
          {t("Transaction successfuly added")}.{" "}
          <div>
            {t("See")}{" "}
            <span
              className="underline cursor-pointer"
              onClick={() => currentView[1]("TABLE")}
            >
              {t("transactions")}
            </span>
          </div>
        </div>
      ),
      { appearance: "success", autoDismiss: true }
    );
  };

  const formValid =
    value.length > 0 &&
    !isNaN(parseFloat(value.replace(",", "."))) &&
    attachToId !== null;

  const setToNow = () => {
    const now = new Date();

    setDate({
      year: now.getFullYear(),
      computerMonth: now.getMonth(),
      day: now.getDate(),
    });

    setHours(now.getHours());

    const nearest15mins = Math.floor(now.getMinutes() / 15) * 15;
    setMinutes(nearest15mins);
  };

  return (
    <>
      <h1 className="text-3xl mt-8 mb-2">📝 {t("Input")}</h1>
      <div className="shadow-xl rounded-xl bg-gray-50 p-10">
        <div className="grid grid-cols-12 gap-2 sm:gap-5">
          <div className="sm:inline-block col-span-12 sm:col-span-3">
            {t("Type")}:
          </div>
          <select
            value={type}
            onChange={(e) =>
              setType(e.target.selectedOptions[0].value as "INCOME" | "EXPENSE")
            }
            className="w-full sm:w-auto border border-gray-400 p-2 outline-none focus:outline-none rounded-md col-span-12 sm:col-span-9"
          >
            <option value="EXPENSE">{t("Expense")}</option>
            <option value="INCOME">{t("Income")}</option>
          </select>
          <div className="sm:inline-block col-span-12 sm:col-span-3 mt-4 sm:mt-0">
            {t("Value")}:
          </div>
          <div className="col-span-12 sm:col-span-9">
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              type="text"
              className="w-full sm:w-auto p-2 rounded-md border-gray-400 outline-none focus:outline-none"
            />
          </div>
          <div className="sm:inline-block col-span-12 sm:col-span-3 mt-4 sm:mt-0">
            {t("Description")}:
          </div>
          <textarea
            spellCheck={false}
            placeholder={t("Description")}
            className="p-2 rounded-md border-gray-400 outline-none focus:outline-none resize-y w-full md:w-6/12 col-span-12 sm:col-span-9"
            rows={1}
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
          />
          <span className="col-span-12 sm:col-span-3 mt-4 sm:mt-0">
            {t("Datetime")}:
          </span>
          <div className="col-span-12 sm:col-span-9">
            <div className="sm:inline-block">
              <DateSelect value={date} onChange={dateChange} />
            </div>
            <select
              onChange={(e) => setHours(parseInt(e.target.value))}
              value={hours}
              className="mr-1 p-2 rounded-md border border-gray-400 outline-none focus:outline-none"
            >
              {new Array(24).fill(0).map((_, i) => (
                <option key={i} value={i}>
                  {`0${i}`.slice(-2)}
                </option>
              ))}
            </select>
            :
            <select
              onChange={(e) => setMinutes(parseInt(e.target.value))}
              value={minutes}
              className="ml-1 p-2 rounded-md border border-gray-400 outline-none focus:outline-none"
            >
              {new Array(4).fill(0).map((_, i) => (
                <option key={i * 15} value={i * 15}>
                  {`0${i * 15}`.slice(-2)}
                </option>
              ))}
            </select>
            <span className="underline ml-4 cursor-pointer" onClick={setToNow}>
              {t("Set to now")}
            </span>
          </div>
          <div className="sm:inline-block col-span-12 sm:col-span-3 mt-4 sm:mt-0">
            {type === "INCOME"
              ? `${t("Income type")}:`
              : `${t("Expense type")}:`}
          </div>
          <div className="col-span-9">
            {type === "EXPENSE" && (
              <select
                value={attachTo}
                onChange={(e) =>
                  setAttachTo(e.target.value as "CATEGORY" | "EVENT")
                }
                className=" border p-2 border-gray-400 outline-none focus:outline-none rounded-md"
              >
                <option value="CATEGORY">{t("Category")}</option>
                <option value="EVENT">{t("Event")}</option>
              </select>
            )}
            {type === "INCOME" ? (
              <select
                value={attachToId || ""}
                onChange={(e) => setAttachToId(e.target.value)}
                className="ml-4 border p-2 border-gray-400 outline-none focus:outline-none rounded-md"
              >
                <option key="-" value="">
                  -
                </option>
                {incomeCategories[0].map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.label}
                  </option>
                ))}
              </select>
            ) : attachTo === "CATEGORY" ? (
              <select
                value={attachToId || ""}
                onChange={(e) => setAttachToId(e.target.value)}
                className="ml-4 border p-2 border-gray-400 outline-none focus:outline-none rounded-md"
              >
                <option key="-" value="">
                  -
                </option>
                {expenseCategories[0].map((et) => (
                  <option key={et.id} value={et.id}>
                    {et.label}
                  </option>
                ))}
              </select>
            ) : (
              <select
                value={attachToId || ""}
                onChange={(e) => setAttachToId(e.target.value)}
                className="ml-4 border p-2 mt-4 border-gray-400 outline-none focus:outline-none rounded-md"
              >
                <option key="-" value="">
                  -
                </option>
                {events[0].map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="mt-4">
          <div className="mt-4 flex items-start"></div>

          <button
            disabled={!formValid}
            onClick={upsertTransaction}
            className={`w-full sm:w-auto mt-10  text-white py-3 sm:px-32 rounded-full ${
              formValid
                ? "bg-green-700 hover:bg-green-600"
                : "bg-gray-500 cursor-not-allowed"
            }`}
          >
            {ut ? t("Update") : t("Add")}
          </button>
        </div>
      </div>
    </>
  );
};

export default Input;
